/**
 * watchTracks.js — single source of truth for the placeholder video
 * library shown on /resources#watch. Other surfaces (Marketplace,
 * DesignDetail) import from here to cross-reference specific tiles
 * without duplicating titles/durations/copy.
 *
 * When real videos land, this is also the right file to swap in
 * `youtubeId` per tile and flip statuses.
 */

export const WATCH_TRACKS = [
  {
    id: "owners",
    eyebrow: "Owners & CDCs",
    sub: "Working through a purchase, from inquiry to keys.",
    tiles: [
      {
        slug: "buy-pre-reviewed-plan",
        title: "How to buy a pre-reviewed plan.",
        body:
          "A 6-minute walk through the marketplace — filtering, " +
          "saving designs, requesting a consultation, signing the " +
          "purchase contract.",
        duration: "6 min",
        status: "first_release",
      },
      {
        slug: "reading-your-contract",
        title: "Reading your purchase contract.",
        body:
          "What you're agreeing to, what stays with the architect, " +
          "and what changes if you build on a different parcel.",
        duration: "4 min",
        status: "coming_soon",
      },
      {
        slug: "city-review-timeline",
        title: "The city review timeline, explained.",
        body:
          "Why pre-reviewed designs clear in 14 days vs. 90+ — and " +
          "what your architect submits on your behalf.",
        duration: "5 min",
        status: "coming_soon",
      },
      {
        slug: "working-with-architect",
        title: "Working with your architect.",
        body:
          "How the consultation works, what questions to ask, and " +
          "where the fixed fee comes from.",
        duration: "4 min",
        status: "coming_soon",
      },
    ],
  },
  {
    id: "architects",
    eyebrow: "Architects",
    sub: "Listing, consulting, and building on the registry.",
    tiles: [
      {
        slug: "submit-design",
        title: "Submitting a design to the registry.",
        body:
          "From application to approval. What City Staff look for, " +
          "and how to package drawings for the first review.",
        duration: "8 min",
        status: "first_release",
      },
      {
        slug: "anatomy-fit-study",
        title: "Anatomy of a fit study.",
        body:
          "Walking the block, measuring setbacks, picking a parcel " +
          "the design actually fits — without months of redrawing.",
        duration: "10 min",
        status: "coming_soon",
      },
      {
        slug: "consultation-economics",
        title: "Consultation economics.",
        body:
          "Pricing the fixed fee, scoping the conversation, and " +
          "the boundary between consultation and full design work.",
        duration: "6 min",
        status: "coming_soon",
      },
    ],
  },
  {
    id: "civic",
    eyebrow: "Civic literacy",
    sub: "Useful even if you never use WHOLE Neighborhoods.",
    tiles: [
      {
        slug: "what-is-pre-reviewed",
        title: "What is pre-reviewed housing?",
        body:
          "The plain-language version. Why this approach exists, " +
          "what problem it actually solves, and where it doesn't fit.",
        duration: "4 min",
        status: "first_release",
      },
      {
        slug: "indianapolis-zoning",
        title: "Indianapolis zoning for infill, explained.",
        body:
          "D-5, D-6, the 4-unit threshold, and what changed in the " +
          "last UDO update.",
        duration: "8 min",
        status: "coming_soon",
      },
      {
        slug: "missing-middle",
        title: "What 'missing middle' actually means.",
        body:
          "The typologies between the single-family home and the " +
          "five-over-one. Why Indianapolis lost them, and what it " +
          "takes to bring them back.",
        duration: "6 min",
        status: "coming_soon",
      },
      {
        slug: "narrow-lots-economics",
        title: "Why narrow lots make economic sense.",
        body:
          "Where the math comes from: land cost, frontage, party " +
          "walls, and the missing rung in the Indianapolis stock.",
        duration: "5 min",
        status: "coming_soon",
      },
    ],
  },
];

export function findWatchTile(slug) {
  for (const track of WATCH_TRACKS) {
    const tile = track.tiles.find((t) => t.slug === slug);
    if (tile) return { ...tile, trackId: track.id, trackEyebrow: track.eyebrow };
  }
  return null;
}
